import { inject } from '@angular/core';
import { CanMatchFn, ResolveFn, Route, Router, Routes } from '@angular/router';
import { BUSINESS_WORKSPACE_ROUTES } from '@core/config/business-workspace.routes';
import { CREATOR_WORKSPACE_ROUTES } from '@core/config/creator-workspace.routes';
import { EDUCATION_WORKSPACE_ROUTES } from '@core/config/education-workspace.routes';
import { LEARNER_WORKSPACE_ROUTES } from '@core/config/learner-workspace.routes';
import { MARKETPLACE_ROUTES } from '@core/config/marketplace.routes';
import { SETTINGS_SHELL_ROUTES } from '@core/config/settings-shell.routes';
import { SHARED_WORKSPACE_ROUTES } from '@core/config/shared-workspace.routes';
import { authGuard, authMatch } from '@core/guards/auth.guard';
import { assessmentAttemptExitGuard } from '@core/guards/assessment-attempt-exit.guard';
import { guestGuard } from '@core/guards/guest.guard';
import { noWorkspaceRedirectGuard } from '@core/guards/no-workspace-redirect.guard';
import { superAdminGuard } from '@core/guards/super-admin.guard';
import { trainingTokenMatch } from '@core/guards/training-token.guard';
import { workspaceGuard } from '@core/guards/workspace.guard';
import { LanguageService } from '@core/services/language.service';
import { environment } from '@environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';

/** Keep prototype/catalog surfaces available to local developers without
 * shipping them as reachable production pages. */
const developmentOnlyMatch: CanMatchFn = () =>
  environment.production ? inject(Router).parseUrl('/404') : true;

/** The registration form is bilingual, so its document title must use the
 * same boot-resolved translation as the rendered form. */
export function resolveRegisterPageTitle() {
  const language = inject(LanguageService);
  const translate = inject(TranslateService);
  language.initFromLocal();

  return translate
    .get('auth.register.title')
    .pipe(map((title) => `Quiziva - ${title}`));
}

export const registerPageTitle: ResolveFn<string> = resolveRegisterPageTitle;

/**
 * Legacy workspace URLs carried a type prefix (`/education`, `/business`,
 * `/creator`, `/learn`). Routes are flat now — the shell renders the right page
 * for the active workspace from state — so strip the prefix and redirect,
 * preserving query params and fragment for emailed / bookmarked deep links.
 */
function legacyWorkspaceRedirect(base: string): Route {
  return {
    path: base,
    children: [
      {
        path: '**',
        redirectTo: (data) => {
          const router = inject(Router);
          const rest = data.url.map((s) => s.path);
          return router.createUrlTree(['/', ...rest], {
            queryParams: data.queryParams,
            fragment: data.fragment ?? undefined,
          });
        },
      },
    ],
  };
}

/**
 * Flat, type-aware workspace pages composed per organization.
 *
 * Order matters for overlapping paths (home, assessments, campaigns,
 * evaluation, exports, participants): education's `canMatch` variants must
 * precede business / shared defaults. See each `*-workspace.routes.ts` file.
 *
 *   education → business → creator → learner → shared → settings → marketplace
 */
const WORKSPACE_PAGES: Routes = [
  ...EDUCATION_WORKSPACE_ROUTES,
  ...BUSINESS_WORKSPACE_ROUTES,
  ...CREATOR_WORKSPACE_ROUTES,
  ...LEARNER_WORKSPACE_ROUTES,
  ...SHARED_WORKSPACE_ROUTES,

  // One settings shell for every persona; nav filtered by permission.
  ...SETTINGS_SHELL_ROUTES,

  // Marketplace: browse, library, pack detail, and pack studio (all workspaces).
  ...MARKETPLACE_ROUTES,

  // Load in-shell (do not redirectTo '/404') so parent auth/workspace guards run
  // first — a redirect would re-enter this shell and loop (NG04016).
  {
    path: '**',
    loadComponent: () =>
      import('./pages/errors/not-found-page/not-found-page.page').then(
        (m) => m.NotFoundPagePage,
      ),
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// App routes
// ═══════════════════════════════════════════════════════════════════════════

export const routes: Routes = [
  // ── Auth & entry ──────────────────────────────────────────────────────────
  // guestGuard: signed-in users (incl. 0 workspaces) never stay on auth forms.
  {
    path: 'login',
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./pages/authentication/login/login.page').then(
        (m) => m.LoginPage,
      ),
  },
  {
    path: 'register',
    title: registerPageTitle,
    canActivate: [guestGuard],
    loadComponent: () =>
      import('./pages/authentication/register/register.page').then(
        (m) => m.RegisterPage,
      ),
  },
  {
    path: 'password-recovery',
    canActivate: [guestGuard],
    loadComponent: () =>
      import(
        './pages/authentication/password-recovery/password-recovery.page'
      ).then((m) => m.PasswordRecoveryPage),
  },
  {
    path: 'reset-password',
    canActivate: [guestGuard],
    loadComponent: () =>
      import(
        './pages/authentication/reset-password/reset-password.page'
      ).then((m) => m.ResetPasswordPage),
  },
  // Join / email / oauth completion — allowed even with zero workspaces.
  {
    path: 'accept-invitation',
    pathMatch: 'full',
    redirectTo: (data) => {
      const router = inject(Router);
      return router.createUrlTree(['/register'], {
        queryParams: data.queryParams,
        fragment: data.fragment ?? undefined,
      });
    },
  },
  {
    path: 'join-link',
    loadComponent: () =>
      import('./pages/authentication/join-link/join-link.page').then(
        (m) => m.JoinLinkPage,
      ),
  },
  {
    path: 'verify-email',
    loadComponent: () =>
      import('./pages/authentication/verify-email/verify-email.page').then(
        (m) => m.VerifyEmailPage,
      ),
  },
  {
    path: 'confirm-email',
    loadComponent: () =>
      import('./pages/authentication/confirm-email/confirm-email.page').then(
        (m) => m.ConfirmEmailPage,
      ),
  },
  {
    path: 'oauth-callback',
    loadComponent: () =>
      import('./pages/authentication/oauth-callback/oauth-callback.page').then(
        (m) => m.OauthCallbackPage,
      ),
  },
  {
    path: 'magic-link',
    loadComponent: () =>
      import('./pages/authentication/magic-link/magic-link.page').then(
        (m) => m.MagicLinkPage,
      ),
  },
  // Legacy /auth/* URLs from emails + old frontend. Pages that read query
  // params load in place — absolute redirectTo would strip the params.
  {
    path: 'auth',
    children: [
      { path: '', pathMatch: 'full', redirectTo: '/login' },
      { path: 'login', redirectTo: '/login', pathMatch: 'full' },
      {
        path: 'registration',
        pathMatch: 'full',
        redirectTo: (data) =>
          inject(Router).createUrlTree(['/register'], {
            queryParams: data.queryParams,
            fragment: data.fragment ?? undefined,
          }),
      },
      {
        path: 'confirm-email',
        loadComponent: () =>
          import('./pages/authentication/confirm-email/confirm-email.page').then(
            (m) => m.ConfirmEmailPage,
          ),
      },
      // These load in place (not redirectTo) because absolute redirects drop
      // the query params the pages read (email, tokens, joinToken).
      {
        path: 'verify-email',
        loadComponent: () =>
          import('./pages/authentication/verify-email/verify-email.page').then(
            (m) => m.VerifyEmailPage,
          ),
      },
      {
        path: 'oauth-callback',
        loadComponent: () =>
          import('./pages/authentication/oauth-callback/oauth-callback.page').then(
            (m) => m.OauthCallbackPage,
          ),
      },
      { path: 'forgot-password', redirectTo: '/password-recovery', pathMatch: 'full' },
      {
        path: 'reset-password',
        loadComponent: () =>
          import(
            './pages/authentication/reset-password/reset-password.page'
          ).then((m) => m.ResetPasswordPage),
      },
      {
        path: 'set-password',
        loadComponent: () =>
          import(
            './pages/authentication/reset-password/reset-password.page'
          ).then((m) => m.ResetPasswordPage),
      },
      {
        path: 'email-login',
        loadComponent: () =>
          import('./pages/authentication/magic-link/magic-link.page').then(
            (m) => m.MagicLinkPage,
          ),
      },
      {
        path: 'join',
        loadComponent: () =>
          import('./pages/authentication/join-link/join-link.page').then(
            (m) => m.JoinLinkPage,
          ),
      },
      {
        path: 'verify/:token',
        loadComponent: () =>
          import('./pages/authentication/magic-link/magic-link.page').then(
            (m) => m.MagicLinkPage,
          ),
      },
      { path: 'verify', redirectTo: '/magic-link', pathMatch: 'full' },
    ],
  },
  // Participant landing for a training program. Anonymous: the page collects
  // an email/phone and signs the participant in (password or OTP) before the
  // materials and the test become available. `trainingTokenMatch` keeps the
  // workspace's own `/training/builder` reachable.
  {
    path: 'training/:token',
    canMatch: [trainingTokenMatch],
    loadComponent: () =>
      import('./pages/assessments/legacy-training-link/assessment-legacy-training-link.page').then(
        (m) => m.AssessmentLegacyTrainingLinkPage,
      ),
  },
  {
    // Anonymous and authenticated visitors use the same capability-scoped
    // participant flow. It must stay above the authenticated workspace shell.
    path: 'assessments/public/:linkGuid',
    canDeactivate: [assessmentAttemptExitGuard],
    loadComponent: () =>
      import(
        './pages/public/assessment-engine/public-assessment-engine.page'
      ).then((m) => m.PublicAssessmentEnginePage),
  },
  {
    path: 'public/access',
    pathMatch: 'full',
    redirectTo: '/404',
  },
  {
    path: 'auth-flows',
    canMatch: [developmentOnlyMatch],
    canActivate: [noWorkspaceRedirectGuard],
    loadComponent: () =>
      import('./pages/authentication/auth-flows/auth-flows.page').then(
        (m) => m.AuthFlowsPage,
      ),
  },

  // ── Public marketing / catalog ─────────────────────────────────────────────
  // Locked if signed in with no workspace.
  {
    path: 'welcome',
    title: 'Quiziva - Evaluări pentru programe de formare',
    canActivate: [noWorkspaceRedirectGuard],
    loadComponent: () =>
      import('./pages/public/home/public-home.page').then(
        (m) => m.PublicHomePage,
      ),
  },
  {
    path: 'pricing',
    title: 'Quiziva - Prețuri',
    canActivate: [noWorkspaceRedirectGuard],
    loadComponent: () =>
      import('./pages/public/pricing/pricing.page').then((m) => m.PricingPage),
  },
  {
    path: 'request-demo',
    title: 'Quiziva - Solicită o ofertă',
    canActivate: [noWorkspaceRedirectGuard],
    loadComponent: () =>
      import('./pages/public/request-demo/request-demo.page').then(
        (m) => m.RequestDemoPage,
      ),
  },
  {
    path: 'public/assessment',
    pathMatch: 'full',
    redirectTo: '/404',
  },
  {
    path: 'certificate',
    pathMatch: 'full',
    redirectTo: '/404',
  },
  {
    path: 'status',
    canActivate: [noWorkspaceRedirectGuard],
    loadComponent: () =>
      import('./pages/public/status/status.page').then((m) => m.StatusPage),
  },
  {
    path: 'all',
    canMatch: [developmentOnlyMatch],
    canActivate: [noWorkspaceRedirectGuard],
    loadComponent: () =>
      import('./pages/demo/all-pages/all-pages.page').then((m) => m.AllPagesPage),
  },
  {
    path: 'modals',
    canMatch: [developmentOnlyMatch],
    canActivate: [noWorkspaceRedirectGuard],
    loadComponent: () =>
      import('./pages/demo/modal-catalog/modal-catalog.page').then(
        (m) => m.ModalCatalogPage,
      ),
  },
  {
    // Live reference for the skeleton / loading system — variants, the state
    // switcher and the primitives, with no API behind them.
    path: 'skeletons',
    canMatch: [developmentOnlyMatch],
    canActivate: [noWorkspaceRedirectGuard],
    loadComponent: () =>
      import('./pages/demo/skeleton-catalog/skeleton-catalog.page').then(
        (m) => m.SkeletonCatalogPage,
      ),
  },

  // ── Workspace selection / creation ─────────────────────────────────────────
  {
    // Auth only — users with pending invites but no accepted membership must reach this hub.
    path: 'select-workspace',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/workspace-selection/workspace-selection.page').then(
        (m) => m.WorkspaceSelectionPage,
      ),
  },
  {
    // Personal Assessment ownership does not require a workspace. Keep this
    // auth-only route above the workspace shell so zero-workspace owners can
    // create, author, publish, and administer their own assessments.
    path: 'my-assessments',
    canActivate: [authGuard],
    children: [
      {
        path: 'library/transfer',
        data: { ownerScope: 'personal' },
        loadComponent: () =>
          import(
            './pages/assessments/library-transfer/assessment-library-transfer.page'
          ).then((m) => m.AssessmentLibraryTransferPage),
      },

      {
        path: 'library',
        data: { ownerScope: 'personal' },
        loadComponent: () =>
          import(
            './pages/assessments/library/assessment-library.page'
          ).then((m) => m.AssessmentLibraryPage),
      },
      {
        path: 'templates',
        data: { ownerScope: 'personal' },
        loadComponent: () =>
          import(
            './pages/assessments/templates/assessment-templates.page'
          ).then((m) => m.AssessmentTemplatesPage),
      },
      {
        path: ':assessmentGuid/releases',
        data: { ownerScope: 'personal' },
        loadComponent: () =>
          import(
            './pages/assessments/release-history/assessment-release-history.page'
          ).then((m) => m.AssessmentReleaseHistoryPage),
      },
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            './pages/assessments/assessment-list/assessment-list.page'
          ).then((m) => m.AssessmentListPage),
      },
      {
        path: ':assessmentGuid/manage',
        data: { ownerScope: 'personal' },
        loadComponent: () =>
          import(
            './pages/assessments/management/assessment-management.page'
          ).then((m) => m.AssessmentManagementPage),
      },
      {
        path: ':assessmentGuid/take',
        data: { ownerScope: 'personal' },
        canDeactivate: [assessmentAttemptExitGuard],
        loadComponent: () =>
          import(
            './pages/assessments/attempt/assessment-attempt.page'
          ).then((m) => m.AssessmentAttemptPage),
      },
      {
        path: ':assessmentGuid/results',
        data: { ownerScope: 'personal' },
        loadComponent: () =>
          import(
            './pages/assessments/results/assessment-results.page'
          ).then((m) => m.AssessmentResultsPage),
      },
    ],
  },
  // Only page a signed-in user with zero workspaces may use (plus join flows).
  {
    path: 'workspace-creation',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./pages/workspace-creation/workspace-creation.page').then(
        (m) => m.WorkspaceCreationPage,
      ),
  },

  // ── Platform (Super Admin) ─────────────────────────────────────────────────
  {
    path: 'platform',
    canActivate: [superAdminGuard],
    loadComponent: () =>
      import('@layouts/platform-layout/platform-layout.component').then(
        (m) => m.PlatformLayoutComponent,
      ),
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./pages/super-admin/dashboard/dashboard.page').then(
            (m) => m.DashboardPage,
          ),
      },
      {
        path: 'health',
        loadComponent: () =>
          import('./pages/super-admin/health/health.page').then(
            (m) => m.HealthPage,
          ),
      },
      {
        path: 'workspaces',
        loadComponent: () =>
          import('./pages/super-admin/workspaces/workspaces.page').then(
            (m) => m.WorkspacesPage,
          ),
      },
      {
        path: 'subscriptions',
        loadComponent: () =>
          import('./pages/super-admin/subscriptions/subscriptions.page').then(
            (m) => m.SubscriptionsPage,
          ),
      },
      {
        path: 'support',
        loadComponent: () =>
          import('./pages/super-admin/support/support.page').then(
            (m) => m.SupportPage,
          ),
      },
      {
        path: 'usage',
        loadComponent: () =>
          import('./pages/assessments/platform/assessment-platform-usage.page').then(
            (m) => m.AssessmentPlatformUsagePage,
          ),
      },
      {
        path: 'users',
        loadComponent: () =>
          import('./pages/super-admin/users/users.page').then(
            (m) => m.UsersPage,
          ),
      },
      {
        path: 'flags',
        loadComponent: () =>
          import('./pages/super-admin/flags/flags.page').then(
            (m) => m.FlagsPage,
          ),
      },
      {
        path: 'comms',
        loadComponent: () =>
          import('./pages/super-admin/comms/comms.page').then(
            (m) => m.CommsPage,
          ),
      },
      {
        path: 'traffic',
        loadComponent: () =>
          import('./pages/super-admin/traffic/traffic.page').then(
            (m) => m.TrafficPage,
          ),
      },
      {
        path: 'daily-quizzes',
        loadComponent: () =>
          import('./pages/super-admin/daily-quizzes/daily-quizzes-admin.page').then(
            (m) => m.DailyQuizzesAdminPage,
          ),
      },
      {
        path: 'operations',
        loadComponent: () =>
          import('./pages/assessments/platform/assessment-platform-operations.page').then(
            (m) => m.AssessmentPlatformOperationsPage,
          ),
      },
      { path: '**', redirectTo: '/404' },
    ],
  },
  // Legacy redirect: /admin/* → /platform/*
  { path: 'admin', redirectTo: 'platform', pathMatch: 'prefix' },

  // ── Legacy workspace-type prefixes → flat URLs ─────────────────────────────
  // Must precede the workspace shell so `/education/*` matches here, not the
  // shell's empty-path children.
  legacyWorkspaceRedirect('education'),
  legacyWorkspaceRedirect('business'),
  legacyWorkspaceRedirect('creator'),
  legacyWorkspaceRedirect('learn'),

  // ── Errors (before the empty-path shell) ───────────────────────────────────
  // The shell path '' would otherwise swallow `/404` and its child `**` would
  // redirect to `/404` again → NG04016 infinite redirect.
  {
    path: '404',
    canActivate: [noWorkspaceRedirectGuard],
    loadComponent: () =>
      import('./pages/errors/not-found-page/not-found-page.page').then(
        (m) => m.NotFoundPagePage,
      ),
  },
  {
    path: '500',
    canActivate: [noWorkspaceRedirectGuard],
    loadComponent: () =>
      import('./pages/errors/server-error-page/server-error-page.page').then(
        (m) => m.ServerErrorPagePage,
      ),
  },
  {
    path: '429',
    canActivate: [noWorkspaceRedirectGuard],
    loadComponent: () =>
      import(
        './pages/errors/too-many-requests-page/too-many-requests-page.page'
      ).then((m) => m.TooManyRequestsPagePage),
  },

  // ── Workspace shell (type/role-aware, flat URLs) ───────────────────────────
  // authMatch runs at recognition so guests redirect to /login before child
  // canMatch / activation — otherwise Ionic leaves ion-router-outlet blank.
  {
    path: '',
    canMatch: [authMatch],
    canActivate: [authGuard, workspaceGuard],
    loadComponent: () =>
      import('@layouts/dashboard-layout/dashboard-layout.component').then(
        (m) => m.DashboardLayoutComponent,
      ),
    children: WORKSPACE_PAGES,
  },
  // Guest fallback if the shell does not match (no JWT).
  { path: '', pathMatch: 'full', redirectTo: 'login' },

  { path: '**', redirectTo: '404' },
];
